import {
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  CircularProgress,
  DialogActions,
  Button,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Close, ArrowBack } from '@mui/icons-material';
import tid from '@/common/infrastructure/config/tid';

interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  content: React.ReactNode;
  contentLoading?: boolean;
  actionLoading?: boolean;
  buttonText: React.ReactNode;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  onConfirmButtonType?: 'submit' | 'button';
  confirmButtonStartIcon?: React.ReactNode;
  onClickBack?: () => void;
  actions?: React.ReactNode;
  /** When true, shows a "Do not show this notification again" checkbox above the actions */
  showDoNotShowAgain?: boolean;
  /** Label for the do-not-show-again checkbox (default: "Do not show this notification again") */
  doNotShowAgainLabel?: string;
  /** Controlled checked state for the do-not-show-again checkbox */
  doNotShowAgainChecked?: boolean;
  /** Called when the do-not-show-again checkbox is toggled */
  onDoNotShowAgainChange?: (checked: boolean) => void;
  dialogMaxWidth?: DialogProps['maxWidth'];
  dialogFullWidth?: boolean;
  paperSx?: SxProps<Theme>;
  dialogContentSx?: SxProps<Theme>;
  dialogActionsSx?: SxProps<Theme>;
  actionButtonsContainerSx?: SxProps<Theme>;
}

interface AlertDialogActionsProps {
  onClose: () => void;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  onConfirmButtonType: 'submit' | 'button';
  actionLoading?: boolean;
  actions?: React.ReactNode;
  buttonText: React.ReactNode;
  confirmButtonStartIcon?: React.ReactNode;
  dialogActionsSx?: SxProps<Theme>;
  actionButtonsContainerSx?: SxProps<Theme>;
}

const AlertDialogActions = ({
  onClose,
  actions,
  buttonText,
  onConfirm,
  confirmDisabled,
  onConfirmButtonType,
  confirmButtonStartIcon,
  actionLoading,
  dialogActionsSx,
  actionButtonsContainerSx
}: AlertDialogActionsProps) => {
  return (
    <DialogActions
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 4,
        py: 2,
        ...dialogActionsSx
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%', ...actionButtonsContainerSx }}>
        <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>
        <Button
          onClick={onClose}
          disabled={actionLoading}
          sx={{
            fontFamily: '"Lato", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
            px: 1,
            py: 0.75,
            borderRadius: '4px',
            color: 'primary.main'
          }}
          {...tid('common:alert-dialog:cancel-button')}
        >
          Cancel
        </Button>
        <Button
          type={onConfirmButtonType}
          startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : confirmButtonStartIcon}
          onClick={onConfirm}
          variant="contained"
          disabled={actionLoading || confirmDisabled}
          sx={{
            fontFamily: '"Lato", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
            px: 2,
            py: 0.75,
            borderRadius: '4px'
          }}
          {...tid('common:alert-dialog:confirm-button')}
        >
          {buttonText}
        </Button>
      </Box>
    </DialogActions>
  );
};

const DO_NOT_SHOW_AGAIN_LABEL = 'Do not show this notification again';

const AlertDialog = ({
  open,
  onClose,
  title,
  subtitle,
  content,
  contentLoading,
  buttonText,
  onConfirm,
  confirmDisabled = false,
  onConfirmButtonType = 'submit',
  confirmButtonStartIcon,
  onClickBack,
  actionLoading,
  actions,
  showDoNotShowAgain = false,
  doNotShowAgainLabel = DO_NOT_SHOW_AGAIN_LABEL,
  doNotShowAgainChecked = false,
  onDoNotShowAgainChange,
  dialogMaxWidth = 'md',
  dialogFullWidth = true,
  paperSx,
  dialogContentSx,
  dialogActionsSx,
  actionButtonsContainerSx
}: AlertDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      maxWidth={dialogMaxWidth}
      fullWidth={dialogFullWidth}
      PaperProps={{
        sx: {
          minHeight: '300px',
          borderRadius: '8px',
          ...paperSx
        }
      }}
    >
      {contentLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle
            sx={{
              p: 0,
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 4,
                py: 3,
                pb: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {onClickBack && (
                  <IconButton
                    onClick={onClickBack}
                    disabled={actionLoading}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'text.primary'
                      }
                    }}
                    {...tid('common:alert-dialog:back-button')}
                  >
                    <ArrowBack />
                  </IconButton>
                )}
                <Stack>
                  <Typography
                    sx={{
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: 700,
                      fontSize: '20px',
                      lineHeight: 1,
                      color: 'text.primary',
                      mb: 0.5
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '20px',
                      color: 'text.primary',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}
                  >
                    {subtitle}
                  </Typography>
                </Stack>
              </Box>
              <IconButton
                onClick={onClose}
                disabled={actionLoading}
                size="small"
                sx={{ p: 0.5 }}
                {...tid('common:alert-dialog:close-button')}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 4, ...dialogContentSx }}>
            {content}
            {showDoNotShowAgain && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={doNotShowAgainChecked}
                    onChange={(_, checked) => onDoNotShowAgainChange?.(checked)}
                    size="small"
                    {...tid('common:alert-dialog:do-not-show-again-checkbox')}
                  />
                }
                label={doNotShowAgainLabel}
                sx={{ mt: 2, display: 'block' }}
              />
            )}
          </DialogContent>
          <AlertDialogActions
            onClose={onClose}
            actions={actions}
            actionLoading={actionLoading}
            buttonText={buttonText}
            onConfirm={onConfirm}
            confirmDisabled={confirmDisabled}
            onConfirmButtonType={onConfirmButtonType}
            confirmButtonStartIcon={confirmButtonStartIcon}
            dialogActionsSx={dialogActionsSx}
            actionButtonsContainerSx={actionButtonsContainerSx}
          />
        </>
      )}
    </Dialog>
  );
};

export default AlertDialog;
